import { Component, HostListener, OnInit } from '@angular/core';
import { AppCommonUtils } from '../../core/helpers/app-common-utils';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AccountService } from '../../features/authentication/services/account.service';
import { UserDetailsInterface } from '../../core/models/user.model';
import { SubscriptionService } from '../../core/services/api/subscription.service';
import { StorageService } from '../../core/services/storage.service';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '../../core/services/dialog.service';
import { combineLatest, filter } from 'rxjs';
import { ProgramFilterService } from '../../core/components/webinar-course/program-filter.service';
import { Meta } from '@angular/platform-browser';
import { ENVIRONMENT } from '../../../environment/environment';

@Component({
    selector: 'app-admin-layout',
    templateUrl: './admin-layout.component.html',
    styleUrl: './admin-layout.component.scss',
})
export class AdminLayoutComponent implements OnInit {
    protected userDetails: UserDetailsInterface = {} as UserDetailsInterface;
    protected hideGotoTopButton = true;
    protected isMobile = AppCommonUtils.isMobileDevice();
    protected isFirstTimeJourney = false;
    protected userLastSteps: number = 0;
    protected isPublicPreviewRoute = false;

    constructor(
        private accountService: AccountService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private subscriptionService: SubscriptionService,
        private storageService: StorageService,
        private dialogService: DialogService,
        private translateService: TranslateService,
        private programFilterService: ProgramFilterService,
        private meta:Meta,
    ) {}

    @HostListener('window:scroll', [])
    onWindowScroll(): void {
        if (document.documentElement.scrollTop > 400) {
            this.hideGotoTopButton = false;
        } else {
            this.hideGotoTopButton = true;
        }
    }

    ngOnInit(): void {
        //this.checkSubscriptionReminder();
        // TPOD-359 (investigation: TPOD-2110): only override the static
        // `noindex, nofollow` in production. Non-prod environments must remain
        // un-indexed; the static tag in `index.{env}.html` is the source of truth there.
        if (ENVIRONMENT.production) {
            this.meta.updateTag({ name: 'robots', content: 'index, follow' });
        }

        this.trackPublicPreviewRoute();

        // Skip user-scoped fetches when the layout is rendering for a logged-out
        // viewer (e.g. shared clip link). Otherwise both endpoints 401 and the
        // response interceptor surfaces an unhandled "session expired" error.
        if (!this.accountService.isAuthenticated()) {
            return;
        }

        this.subscriptionService.fetchAndStoreSubscriptionDetails().subscribe();
        this.getUserDetails();
        //this.initFirstTimeJourneyUi();
    }

    private trackPublicPreviewRoute(): void {
        const isPreview = (url: string) => url.startsWith('/clips/preview/');
        this.isPublicPreviewRoute = isPreview(this.router.url);
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event) => {
                this.isPublicPreviewRoute = isPreview(event.urlAfterRedirects);
            });
    }

    protected gotoTopScreen(): void {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    }

    private getUserDetails(): void {
        this.accountService.getUserDetails().subscribe({
            next: (resp) => {
                if (resp) {
                    this.userDetails = resp;
                    // this.userLastSteps = this.userDetails.currentTutorialStep;

                    // this.getFirstTimeJourneyData();
                }
            },
        });
    }

    private getFirstTimeJourneyData(): void {
        const showFirstTimeJourney = !this.userDetails.isTutorialFinish;

        if (showFirstTimeJourney) {
            this.router.navigate(['/home'], {
                queryParams: { firstTimeJourney: 'true' },
                queryParamsHandling: 'merge',
            });
        }
    }

    private initFirstTimeJourneyUi(): void {
        this.activatedRoute.queryParams.subscribe((params) => {
            const firstTimeJourneyParam = params['firstTimeJourney'];

            this.isFirstTimeJourney = firstTimeJourneyParam === 'true';
        });
    }

    private checkSubscriptionReminder(): void {
        combineLatest([this.subscriptionService.subscriptionDetails$, this.subscriptionService.aaeDetails$]).subscribe(
            ([subscriptionDetails, aaeDetails]) => {
                if (subscriptionDetails?.nextBillingDate && subscriptionDetails?.subscriptionStartDate && aaeDetails) {
                    const subscriptionEndDate = moment(subscriptionDetails.nextBillingDate).utc().startOf('day');
                    const today = moment().utc().startOf('day');
                    const todayDate = today.format('YYYY-MM-DD');

                    if (!this.storageService.lastCheckedDate || this.storageService.lastCheckedDate !== todayDate) {
                        const monthsDiff = subscriptionEndDate.diff(today, 'months', true);
                        const daysDiff = subscriptionEndDate.diff(today, 'days');
                        const isExactMonths =
                            Math.abs(monthsDiff - 9) < 0.1 ||
                            Math.abs(monthsDiff - 6) < 0.1 ||
                            Math.abs(monthsDiff - 3) < 0.1;
                        const isExactDays = daysDiff === 60 || daysDiff === 45;

                        if (isExactMonths || isExactDays) {
                            this.dialogService
                                .showReminderPopup({
                                    title: 'text.subscriptionEndReminderTitle',
                                    titleClass: '!font-semibold w-[25rem]',
                                    message: this.translateService.instant('text.subscriptionEndReminderMessage', {
                                        endDate: moment(subscriptionDetails.nextBillingDate)
                                            .subtract(15, 'days')
                                            .format('DD/MM/YYYY'),
                                        minutes: aaeDetails.remainingMinutes,
                                    }),
                                    cancelText: 'button.close',
                                    confirmText: 'button.submitQuestion',
                                    icon: 'bg-subscription-end-reminder-popup',
                                })
                                .pipe(filter((isConfirm) => isConfirm))
                                .subscribe(() => this.router.navigate(['/ask-an-expert']));
                        }

                        this.storageService.setLastCheckedDate(todayDate);
                    }
                }
            },
        );
    }
}
